import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  DateOption,
  DateRange,
  DateRangeSelectorService,
  MomentDateRange,
  QuarterOption,
} from './date-range-selector.service';
import moment from 'moment';
import { Observable, Subscription } from 'rxjs';
import { DATE_FILTERS, DateFilterOptions, QUARTER_FILTERS, QuarterFilterOption, SEASON_FILTERS } from './constants';

@Component({
  templateUrl: './date-range-selector.component.html',
  styleUrls: ['./date-range-selector.component.scss'],
})
export class DateRangeSelectorComponent implements OnInit, OnDestroy {
  filterOptions: DateOption[] = [...DATE_FILTERS];

  seasonOptions: DateOption[] = [...SEASON_FILTERS];

  selected: DateFilterOptions;
  customDate: MomentDateRange = {
    start: moment(),
    end: moment(),
  };

  compareDateRange: DateRange = {
    start: new Date(),
    end: new Date(),
  };

  quarters: QuarterOption[] = [...QUARTER_FILTERS];

  monthList: Date[] = [];
  yearList: number[] = [];

  maxEndDate = moment();
  maxEndCompareDate = new Date();

  currentMonth = moment().month(1).toDate();
  currentQuarter = QuarterFilterOption.First_Quarter;
  currentYear = new Date().getFullYear();

  private customDateSub: Subscription[] = [];

  comparableDatePicker$: Observable<any>;

  compareDateRange$: Observable<any> = this.dateRangeService.getCompareDate();

  seasonDatePickerType: string;

  constructor(
    public dialogRef: MatDialogRef<DateRangeSelectorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DateOption,
    public dateRangeService: DateRangeSelectorService,
  ) {}

  ngOnInit(): void {
    this.selected = this.dateRangeService.getKey();
    this.comparableDatePicker$ = this.dateRangeService.getDatePickerType();
    this.customDateSub.push(
      this.dateRangeService.getFilter().subscribe((filter) => {
        this.customDate.start = moment(filter.start);
        this.customDate.end = moment(filter.end);
      }),
    );
    this.customDateSub.push(
      this.dateRangeService.getCompareFilter().subscribe((filter) => {
        this.compareDateRange.start = filter.start;
        this.compareDateRange.end = filter.end;
        this.currentMonth = moment().month(filter.end.getMonth()).toDate();
        this.currentYear = filter.start.getFullYear();
        this.dateRangeService.updatePanelCompareDate(this.selected, filter.start, filter.end);
        if (this.selected === DateFilterOptions.Quarter) {
          this.currentQuarter = QUARTER_FILTERS.find(
            (entry) => filter.start.getMonth() + 1 === entry.startMonth,
          ).section;
        } else {
          this.currentQuarter = QuarterFilterOption.First_Quarter;
        }
      }),
    );
    this.updateSeasonDate(this.selected);
    this.initializeList();
  }

  ngOnDestroy(): void {
    this.customDateSub.forEach((subs) => subs.unsubscribe());
  }

  submit(): void {
    if (this.selected === DateFilterOptions.Custom) {
      this.dateRangeService.setAsCustom(this.customDate, this.compareDateRange);
    } else if (this.selected === DateFilterOptions.Month) {
      this.dateRangeService.setAsMonthly(this.compareDateRange);
    } else if (this.selected === DateFilterOptions.Quarter) {
      this.dateRangeService.setAsQuarter(this.compareDateRange);
    } else {
      this.dateRangeService.setKey(this.selected);
    }
    this.dialogRef.close();
  }

  onCustomStartChange(momentDate: moment.Moment, date?: Date): void {
    this.selected = DateFilterOptions.Custom;
    this.customDate.start = momentDate;
    this.compareDateRange.start = date;
    this.dateRangeService.updatePanelCompareDate(this.selected, this.compareDateRange.start, this.compareDateRange.end);
  }

  onCustomEndChange(momentDate: moment.Moment, date?: Date): void {
    this.selected = DateFilterOptions.Custom;
    this.customDate.end = momentDate;
    this.compareDateRange.end = date;
    this.dateRangeService.updatePanelCompareDate(this.selected, this.compareDateRange.start, this.compareDateRange.end);
  }

  onMonthSelectChanged(selectedMonth?: any, selectedYear?: any): void {
    let month, year;
    if (selectedMonth) {
      month = moment().month(selectedMonth).format('M');
      month -= 1;
      this.currentMonth = moment().month(month).toDate();
      year = this.currentYear;
    } else if (selectedYear) {
      year = selectedYear;
      this.currentYear = selectedYear;
      month = this.currentMonth.getMonth();
    } else {
      year = this.currentYear;
      month = this.currentMonth.getMonth();
    }
    this.compareDateRange.start = moment().year(year).month(month).startOf('month').toDate();
    this.compareDateRange.end = moment().year(year).month(month).endOf('month').toDate();
    this.dateRangeService.updatePanelCompareDate(
      DateFilterOptions.Month,
      this.compareDateRange.start,
      this.compareDateRange.end,
    );
  }

  onQuarterSelectChanged(selectedQuarter?: QuarterFilterOption, selectedYear?: number): void {
    let quarter, year;
    if (selectedQuarter != null) {
      this.currentQuarter = selectedQuarter;
      quarter = selectedQuarter;
      year = this.compareDateRange.start.getFullYear();
    } else if (selectedYear) {
      this.currentYear = selectedYear;
      year = selectedYear;
      quarter = this.currentQuarter;
    } else {
      quarter = this.currentQuarter;
      year = this.currentYear;
    }
    const startMonth = QUARTER_FILTERS.find((entry) => quarter === entry.section).startMonth;
    const endMonth = QUARTER_FILTERS.find((entry) => quarter === entry.section).endMonth;
    this.compareDateRange.start = moment()
      .year(year)
      .month(startMonth - 1)
      .startOf('month')
      .toDate();
    this.compareDateRange.end = moment()
      .year(year)
      .month(endMonth - 1)
      .endOf('month')
      .toDate();
    this.dateRangeService.updatePanelCompareDate(
      DateFilterOptions.Quarter,
      this.compareDateRange.start,
      this.compareDateRange.end,
    );
  }

  updateDateRangeData(selectedKey: DateFilterOptions): void {
    if (selectedKey !== DateFilterOptions.Custom) {
      this.compareDateRange.start = this.dateRangeService.updateStartDate(selectedKey);
      this.compareDateRange.end = moment().startOf('day').toDate();
      this.dateRangeService.updatePanelCompareDate(selectedKey, this.compareDateRange.start, this.compareDateRange.end);
    }
    this.seasonDatePickerType = '';
  }

  updateSeasonDate(selectedKey: DateFilterOptions): void {
    if (selectedKey === DateFilterOptions.Month) {
      this.seasonDatePickerType = 'Month';
      this.onMonthSelectChanged();
    } else if (selectedKey === DateFilterOptions.Quarter) {
      this.seasonDatePickerType = 'Quarter';
      this.onQuarterSelectChanged();
    } else {
      this.seasonDatePickerType = '';
    }
  }

  initializeList(): void {
    for (let i = 2000; i <= new Date().getFullYear(); i++) {
      this.yearList.push(i);
    }

    for (let s = 1; s <= 12; s++) {
      this.monthList.push(
        moment()
          .month(s - 1)
          .toDate(),
      );
    }
  }
}
